/**
 * Device Information Collector
 * Collects browser and device characteristics for fingerprinting
 */

import logger from "./logger";

export interface DeviceFingerprint {
  userAgent: string;
  platform: string;
  language: string;
  screenResolution: string;
  timezone: string;
  plugins: string[];
  canvas: string;
}

/**
 * Get browser user agent
 */
export const getUserAgent = (): string => {
  return navigator.userAgent;
};

/**
 * Get platform information
 */
export const getPlatform = (): string => {
  return navigator.platform || "Unknown";
};

/**
 * Get browser language
 */
export const getLanguage = (): string => {
  return navigator.language || "en-US";
};

/**
 * Get screen resolution
 */
export const getScreenResolution = (): string => {
  return `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
};

/**
 * Get timezone
 */
export const getTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get installed plugins (deprecated but useful for fingerprinting)
 */
export const getPlugins = (): string[] => {
  if (!navigator.plugins || navigator.plugins.length === 0) {
    return [];
  }

  const plugins: string[] = [];
  for (let i = 0; i < navigator.plugins.length; i++) {
    const plugin = navigator.plugins[i];
    if (plugin && plugin.name) {
      plugins.push(plugin.name);
    }
  }

  return plugins.sort();
};

/**
 * Generate canvas fingerprint
 */
export const getCanvasFingerprint = (): string => {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return "no-canvas";
    }

    // Draw text with varying styles
    const text = "EEG Portal Security 🔒";
    ctx.textBaseline = "top";
    ctx.font = '14px "Arial"';
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText(text, 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText(text, 4, 17);

    // Convert to data URL
    const dataUrl = canvas.toDataURL();

    // Generate hash from data URL
    let hash = 0;
    for (let i = 0; i < dataUrl.length; i++) {
      const char = dataUrl.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    return hash.toString();
  } catch (error) {
    logger.warn("Canvas fingerprint failed:", error);
    return "canvas-error";
  }
};

/**
 * Collect all device information
 */
export const collectDeviceInfo = (): DeviceFingerprint => {
  return {
    userAgent: getUserAgent(),
    platform: getPlatform(),
    language: getLanguage(),
    screenResolution: getScreenResolution(),
    timezone: getTimezone(),
    plugins: getPlugins(),
    canvas: getCanvasFingerprint(),
  };
};

/**
 * Generate a simple hash from device fingerprint
 * (Server will generate the actual secure hash)
 */
export const generateClientHash = (fingerprint: DeviceFingerprint): string => {
  const str = JSON.stringify(fingerprint);
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  return Math.abs(hash).toString(16);
};
